import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Card, Form, Input, Button, message, Typography, Row, Col, Space, Upload } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { fetchMosads, createMosad } from '../../redux/slices/mosadsSlice';
import styles from './NewMosad.module.css';
import { useTranslation } from '../../i18n';

const { Title, Text } = Typography;
const { TextArea } = Input;

const NewMosad = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Convert logo file to base64 if uploaded
      let logoUrl = values.mosad_logo; // todo: check this since it is saved in fake path
      if (logoFile) {
        logoUrl = await convertToBase64(logoFile);
      }
      
      const mosadData = {
        ...values,
        mosad_logo: logoUrl // todo: check this
      };
      
      const result = await dispatch(createMosad(mosadData));
      if (result.payload.success) {
        message.success(t('notifications.frontend.common.mosadCreatedSuccessfully'));
        await dispatch(fetchMosads());
        navigate('/mosads');
      } else {
        message.error(result.payload.message || t('notifications.frontend.common.failedToCreateMosad'));
      }
    } catch (error) {
      message.error(t('notifications.frontend.common.errorCreatingMosad'));
      console.error('Error creating mosad:', error);
    } finally {
      setLoading(false);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleLogoUpload = (info) => {
    const { file } = info;
    
    // Validate file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error(t('notifications.frontend.common.imageFilesOnly'));
      return false;
    }
    
    // Validate file size (max 2MB)
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(t('notifications.frontend.common.imageMax2mb'));
      return false;
    }
    
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    return false; // Prevent default upload
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    form.setFieldsValue({ mosad_logo: '' });
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
    message.error(t('notifications.frontend.common.fillRequiredFields'));
  };

  const handleCancel = () => {
    navigate('/mosads');
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.header}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            className={styles.backButton}
          >
            {t('ui.mosads.backToMosads')}
          </Button>
          <Title level={2} className={styles.title}>{t('ui.mosads.createTitle')}</Title>
        </div>

        <Form
          form={form}
          name="createMosad"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
          className={styles.form}
          size="middle"
        >
          <Row gutter={[14, 10]}>
            {/* Basic Information */}
            <Col span={24}>
              <Title level={4} className={styles.sectionTitle}>{t('ui.mosads.basicInfoSection')}</Title>
            </Col>
            
            <Col xs={24} md={12}>
              <Form.Item
                label={t('ui.mosads.mosadName')}
                name="mosad_name"
                rules={[
                  { required: true, message: 'Please enter the mosad name' },
                  { min: 2, message: 'Name must be at least 2 characters' }
                ]}
              >
                <Input placeholder={t('ui.mosads.mosadNamePlaceholder')} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t('ui.mosads.mosadCode')}
                name="mosad_code"
                rules={[
                  { required: true, message: 'Please enter the mosad code' },
                  { pattern: /^[A-Z0-9]+$/, message: 'Code must contain only uppercase letters and numbers' },
                  { min: 3, message: 'Code must be at least 3 characters' }
                ]}
              >
                <Input placeholder="12744" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t('ui.mosads.businessId')}
                name="mosad_business_id"
                rules={[
                  { pattern: /^[0-9]+$/, message: 'Business ID must contain only numbers' }
                ]}
              >
                <Input placeholder={t('ui.mosads.businessIdPlaceholder')} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t('ui.mosads.logo')}
                name="mosad_logo"
              >
                <div className={styles.logoUploadContainer}>
                  {logoPreview ? (
                    <div className={styles.logoPreview}>
                      <img src={logoPreview} alt="Logo preview" className={styles.logoImage} />
                      <Button 
                        type="text" 
                        danger 
                        size="small" 
                        onClick={removeLogo}
                        className={styles.removeLogoBtn}
                      >
                        {t('ui.mosads.remove')}
                      </Button>
                    </div>
                  ) : (
                    <Upload
                      beforeUpload={handleLogoUpload}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />} className={styles.uploadButton}>
                        {t('ui.mosads.uploadLogo')}
                      </Button>
                    </Upload>
                  )}
                  <div className={styles.uploadHint}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {t('ui.mosads.uploadHint')}
                    </Text>
                  </div>
                </div>
              </Form.Item>
            </Col>

            {/* Bank Information */}
            <Col span={24}>
              <Title level={4} className={styles.sectionTitle}>{t('ui.mosads.bankInfoSection')}</Title>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={t('ui.mosads.bankCode')}
                name="mosad_bank_code"
                rules={[
                  { required: true, message: 'Please enter the bank code' },
                  { pattern: /^[0-9]+$/, message: 'Bank code must contain only numbers' },
                  { len: 3, message: 'Bank code must be exactly 2 digits' }
                ]}
              >
                <Input placeholder="012" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={t('ui.mosads.bankBranch')}
                name="mosad_bank_branch"
                rules={[
                  { required: true, message: 'Please enter the bank branch' },
                  { pattern: /^[0-9]+$/, message: 'Bank branch must contain only numbers' },
                  { len: 3, message: 'Bank branch must be exactly 3 digits' }
                ]}
              >
                <Input placeholder="001" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={t('ui.mosads.bankAccount')}
                name="mosad_bank_account"
                rules={[
                  { required: true, message: 'Please enter the bank account number' },
                  { pattern: /^[0-9]+$/, message: 'Account number must contain only numbers' },
                  { min: 5, message: 'Account number must be at least 5 digits' }
                ]}
              >
                <Input placeholder="1234567890" />
              </Form.Item>
            </Col>

            {/* Additional Settings */}
            {/* <Col span={24}>
              <Title level={4} className={styles.sectionTitle}>Additional Settings</Title>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Transaction Ascending Number"
                name="mosad_tx_asc_num"
                rules={[
                  { type: 'number', message: 'Must be a valid number' },
                  { min: 1, message: 'Must be at least 1' }
                ]}
                initialValue={1}
              >
                <Input type="number" placeholder="1" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Active Status"
                name="isActive"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch 
                  checkedChildren="Active" 
                  unCheckedChildren="Inactive"
                />
              </Form.Item>
            </Col> */}
          </Row>

          <div className={styles.formActions}>
            <Space size="middle">
              <Button 
                size="middle" 
                onClick={handleCancel}
                className={styles.cancelButton}
              >
                {t('ui.mosads.cancel')}
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<SaveOutlined />}
                size="middle"
                className={styles.submitButton}
              >
                {t('ui.mosads.create')}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default NewMosad;
